import { IEntity } from '../../types'
import { createRNG } from './seededRandom'

const GRID_SIZE = 16
const DIRECTIONS = [0, 4, 8, 12]

const ENTITY_POOL = [
    'transport-belt',
    'underground-belt',
    'assembling-machine-1',
    'inserter',
    'long-handed-inserter',
    'small-electric-pole',
    'wooden-chest',
    'pipe',
    'pipe-to-ground',
    'lab',
]

const RECIPES = [
    'iron-gear-wheel',
    'copper-cable',
    'iron-stick',
    'electronic-circuit',
    'transport-belt',
    'inserter',
    'pipe',
    'underground-belt',
    'long-handed-inserter',
    'small-electric-pole',
    'wooden-chest',
    'pipe-to-ground',
    'assembling-machine-1',
    'lab',
    'automation-science-pack',
    'logistic-science-pack',
]

const ENTITY_SIZES: Record<string, [number, number]> = {
    'transport-belt': [1, 1],
    'underground-belt': [1, 1],
    'assembling-machine-1': [3, 3],
    'inserter': [1, 1],
    'long-handed-inserter': [1, 1],
    'small-electric-pole': [1, 1],
    'wooden-chest': [1, 1],
    'pipe': [1, 1],
    'pipe-to-ground': [1, 1],
    'lab': [3, 3],
}

const DIRECTIONAL_ENTITIES = new Set([
    'transport-belt', 'underground-belt', 'inserter',
    'long-handed-inserter', 'pipe-to-ground',
])

// ---- Bigint encode / decode ----
// Format: [6-bit count] [18-bit entity]*
// Entity: type(4) | x(4) | y(4) | dir(2) | recipe(4)

function entityToBits(e: IEntity): bigint {
    const typeIdx = ENTITY_POOL.indexOf(e.name)
    const dirIdx = DIRECTIONS.indexOf(e.direction ?? 0)
    const recipeIdx = e.recipe ? RECIPES.indexOf(e.recipe) : 0
    return (
        (BigInt(typeIdx >= 0 ? typeIdx : 0)) |
        (BigInt(e.position.x & 0xf) << 4n) |
        (BigInt(e.position.y & 0xf) << 8n) |
        (BigInt(dirIdx >= 0 ? dirIdx : 0) << 12n) |
        (BigInt(recipeIdx >= 0 ? recipeIdx : 0) << 14n)
    )
}

function bitsToEntity(bits: bigint, entityNumber: number): IEntity {
    const typeIdx = Number(bits & 0xfn)
    const x = Number((bits >> 4n) & 0xfn)
    const y = Number((bits >> 8n) & 0xfn)
    const dirIdx = Number((bits >> 12n) & 0x3n)
    const recipeIdx = Number((bits >> 14n) & 0xfn)

    const name = ENTITY_POOL[typeIdx] || ENTITY_POOL[0]
    const entity: IEntity = {
        entity_number: entityNumber,
        name,
        position: { x, y },
    }

    if (DIRECTIONAL_ENTITIES.has(name)) {
        entity.direction = DIRECTIONS[dirIdx] || 0
    }

    if (name === 'assembling-machine-1' || name === 'lab') {
        entity.recipe = RECIPES[recipeIdx] || RECIPES[0]
    }

    return entity
}

/** Encode entities into a bigint seed.
 *  Format: [entity_0:18][entity_1:18]...[entity_N:18][count:6]
 *  count occupies the lowest 6 bits; entity 0 starts at bit 6. */
export function encodeFactory(entities: IEntity[]): bigint {
    let seed = BigInt(entities.length) & 0x3fn
    for (let i = 0; i < entities.length; i++) {
        seed |= entityToBits(entities[i]) << (6n + BigInt(i) * 18n)
    }
    return seed
}

/** Decode a bigint seed back into entities with collision resolution.
 *  Entities are spread across the grid so every seed produces a valid layout. */
export function decodeFactory(seed: bigint): IEntity[] {
    const count = Number(seed & 0x3fn)
    const occupied = new Set<string>()
    const entities: IEntity[] = []

    for (let i = 0; i < count && entities.length < 63; i++) {
        const bits = (seed >> (6n + BigInt(i) * 18n)) & 0x3ffffn
        const entity = bitsToEntity(bits, entities.length + 1)
        const [w, h] = ENTITY_SIZES[entity.name] || [1, 1]

        // Try the encoded position first, then scan for a free spot
        let placed = false
        const startX = entity.position.x % GRID_SIZE
        const startY = entity.position.y % GRID_SIZE

        for (let offset = 0; offset < GRID_SIZE * GRID_SIZE && !placed; offset++) {
            const nx = (startX + offset) % GRID_SIZE
            const ny = (startY + Math.floor((startX + offset) / GRID_SIZE)) % GRID_SIZE
            if (canPlace(nx, ny, w, h, occupied)) {
                entity.position = { x: nx, y: ny }
                placed = true
            }
        }
        if (!placed) continue // grid full

        for (const t of tilesOccupied(entity.position.x, entity.position.y, w, h)) {
            occupied.add(t)
        }
        entities.push(entity)
    }
    return entities
}

// ---- Random factory generation (with collision avoidance) ----

function tilesOccupied(x: number, y: number, w: number, h: number): string[] {
    const tiles: string[] = []
    const sx = x - Math.floor(w / 2)
    const sy = y - Math.floor(h / 2)
    for (let dx = 0; dx < w; dx++)
        for (let dy = 0; dy < h; dy++)
            tiles.push(`${sx + dx},${sy + dy}`)
    return tiles
}

function canPlace(x: number, y: number, w: number, h: number, occupied: Set<string>): boolean {
    const sx = x - Math.floor(w / 2)
    const sy = y - Math.floor(h / 2)
    if (sx < 0 || sy < 0 || sx + w > GRID_SIZE || sy + h > GRID_SIZE) return false
    for (const t of tilesOccupied(x, y, w, h)) if (occupied.has(t)) return false
    return true
}

function generateRandomEntities(rng: () => number): IEntity[] {
    const occupied = new Set<string>()
    const entities: IEntity[] = []
    const target = 12 + Math.floor(rng() * 29)
    let attempts = 0

    while (entities.length < target && attempts < target * 10) {
        attempts++
        const name = ENTITY_POOL[Math.floor(rng() * ENTITY_POOL.length)]
        const dir = DIRECTIONS[Math.floor(rng() * DIRECTIONS.length)]
        const [w, h] = ENTITY_SIZES[name] || [1, 1]

        for (let retry = 0; retry < 10; retry++) {
            const x = Math.floor(rng() * GRID_SIZE)
            const y = Math.floor(rng() * GRID_SIZE)
            if (!canPlace(x, y, w, h, occupied)) continue

            for (const t of tilesOccupied(x, y, w, h)) occupied.add(t)
            const entity: IEntity = {
                entity_number: entities.length + 1,
                name,
                position: { x, y },
            }
            if (DIRECTIONAL_ENTITIES.has(name)) entity.direction = dir
            if (name === 'assembling-machine-1' || name === 'lab')
                entity.recipe = RECIPES[Math.floor(rng() * RECIPES.length)]

            entities.push(entity)
            break
        }
    }
    return entities
}

// ---- Public API ----

export interface FactoryGenerationResult {
    entities: IEntity[]
    seed: bigint
}

/** Hash tile coordinates into a deterministic 32-bit seed */
export function hashTile(tx: number, ty: number): number {
    let h = ((tx * 374761393 + ty * 668265263) | 0) >>> 0
    h = Math.imul(h ^ (h >>> 13), 1274126177)
    return (h ^ (h >>> 16)) >>> 0
}

/** Generate a random collision-free factory from a PRNG seed (fully deterministic). */
export function generateFactoryFromSeed(rngSeed: number): FactoryGenerationResult {
    const rng = createRNG(rngSeed)
    const entities = generateRandomEntities(rng)
    const seed = encodeFactory(entities)
    return { entities, seed }
}

/** Generate a random collision-free factory. Returns the seed (encoded bigint). */
export function generateFactory(): FactoryGenerationResult {
    // Use Math.random as entropy source, convert to hardware seed
    const hwSeed = Math.floor(Math.random() * 2147483647)
    const rng = createRNG(hwSeed)
    const entities = generateRandomEntities(rng)
    const seed = encodeFactory(entities)
    return { entities, seed }
}

/** Load a factory from a bigint seed */
export function loadFactory(seed: bigint): FactoryGenerationResult {
    const entities = decodeFactory(seed)
    return { entities, seed }
}

/** Hash all bits of a bigint into a 32-bit integer for RNG seeding */
function hashBigInt(n: bigint): number {
    let hash = 0
    while (n > 0n) {
        hash ^= Number(n & 0xffffffffn)
        n >>= 32n
    }
    return hash >>> 0
}

/** Navigate to a similar seed by mutating one entity field.
 *  Deterministically picks which entity and field to cycle based on the seed itself.
 *  Fields: type, x, y, direction, recipe — or add/remove an entity. */
export function neighborSeed(seed: bigint, direction: 'prev' | 'next'): bigint {
    const count = Number(seed & 0x3fn)
    const delta = direction === 'next' ? 1 : -1

    // Empty seed: next adds one entity, prev stays empty
    if (count === 0) {
        return direction === 'next' ? 1n : 0n
    }

    // Hash ALL bits so any mutation changes which field gets picked next
    const rng = createRNG(hashBigInt(seed) ^ (direction === 'next' ? 1 : 0))

    // Pick field to mutate: 0=type, 1=x, 2=y, 3=dir, 4=recipe, 5=add/remove
    const FIELD_COUNT = 6
    const field = Math.floor(rng() * FIELD_COUNT)

    // Mutate count (add/remove entity)
    if (field === 5) {
        const newCount = Math.max(0, Math.min(63, count + delta))
        if (newCount === count) return seed
        return (seed & ~0x3fn) | BigInt(newCount)
    }

    const entityIdx = Math.floor(rng() * count)
    const shift = 6n + BigInt(entityIdx) * 18n
    const entityBits = (seed >> shift) & 0x3ffffn

    // Clear the entity slot
    const lowMask = (1n << shift) - 1n
    const lowPart = seed & lowMask
    const highPart = (seed >> (shift + 18n)) << (shift + 18n)

    let newEntityBits = entityBits
    switch (field) {
        case 0: { // type (bits 0-3)
            const v = (Number(entityBits & 0xfn) + delta + ENTITY_POOL.length) % ENTITY_POOL.length
            newEntityBits = (entityBits & ~0xfn) | BigInt(v)
            break
        }
        case 1: { // x (bits 4-7)
            const v = (Number((entityBits >> 4n) & 0xfn) + delta + 16) % 16
            newEntityBits = (entityBits & ~(0xfn << 4n)) | (BigInt(v) << 4n)
            break
        }
        case 2: { // y (bits 8-11)
            const v = (Number((entityBits >> 8n) & 0xfn) + delta + 16) % 16
            newEntityBits = (entityBits & ~(0xfn << 8n)) | (BigInt(v) << 8n)
            break
        }
        case 3: { // direction (bits 12-13)
            const v = (Number((entityBits >> 12n) & 0x3n) + delta + 4) % 4
            newEntityBits = (entityBits & ~(0x3n << 12n)) | (BigInt(v) << 12n)
            break
        }
        case 4: { // recipe (bits 14-17)
            const v = (Number((entityBits >> 14n) & 0xfn) + delta + RECIPES.length) % RECIPES.length
            newEntityBits = (entityBits & ~(0xfn << 14n)) | (BigInt(v) << 14n)
            break
        }
    }

    return highPart | (newEntityBits << shift) | lowPart
}

// ---- Base36 helpers for URL-friendly seeds ----

const BASE36 = '0123456789abcdefghijklmnopqrstuvwxyz'

export function seedToBase36(seed: bigint): string {
    if (seed === 0n) return '0'
    let result = ''
    let n = seed
    while (n > 0n) {
        result = BASE36[Number(n % 36n)] + result
        n /= 36n
    }
    return result
}

export function base36ToSeed(str: string): bigint {
    str = str.toLowerCase().replace(/[^0-9a-z]/g, '')
    let result = 0n
    for (const c of str) {
        result = result * 36n + BigInt(BASE36.indexOf(c))
    }
    return result
}
